import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud } from "lucide-react";
import { uploadMedia } from "../../services/galleryService";
import { formatFileSize } from "../../utils/helpers";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import { toast } from "react-toastify";
import PrivacyPolicyModal, { hasAcceptedPrivacy } from "../../components/common/PrivacyPolicyModal";
import { useAuth } from "../../context/AuthContext";
import { color, radius } from "../../styles/theme";

const UploadMedia = () => {
  const { t } = useTranslation(["gallery", "common"]);
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const doUpload = async () => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    setUploading(true);
    try {
      await uploadMedia(formData);
      toast.success(t("uploadMedia.toastSuccess"));
      setFiles([]);
    } catch {
      toast.error(t("uploadMedia.toastError"));
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = () => {
    if (files.length === 0) {
      toast.warning(t("uploadMedia.toastSelectFile"));
      return;
    }
    if (hasAcceptedPrivacy(user?.Email)) {
      doUpload();
    } else {
      setShowPrivacyModal(true);
    }
  };

  return (
    <>
      {showPrivacyModal && (
        <PrivacyPolicyModal
          onAccept={() => { setShowPrivacyModal(false); doUpload(); }}
          onDecline={() => setShowPrivacyModal(false)}
        />
      )}
      <div>
        <h2 style={s.heading}>{t("uploadMedia.heading")}</h2>

        <Card style={s.card}>
          <label style={s.dropzone}>
            <input
              type="file"
              multiple
              accept="image/*,video/*,.mov,.avi,.mkv,.wmv,.webm,.flv,.m4v,.3gp,.ts,.mts"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <UploadCloud size={26} strokeWidth={1.75} color={color.primary500} />
            <span style={s.dropText}>{t("uploadMedia.dropzoneText")}</span>
          </label>

          {files.length > 0 && (
            <ul style={s.fileList}>
              {files.map((f, i) => (
                <li key={i} style={s.fileItem}>
                  <span>{f.name}</span>
                  <span style={s.size}>{formatFileSize(f.size)}</span>
                </li>
              ))}
            </ul>
          )}

          <Button onClick={handleUpload} disabled={uploading} style={{ marginTop: 20 }}>
            {uploading
              ? t("uploadMedia.uploading")
              : files.length > 0
              ? t("uploadMedia.uploadButtonWithCount", { count: files.length })
              : t("common:actions.upload")}
          </Button>

          <p style={s.uploadNote}>
            {t("uploadMedia.uploadNote")}
          </p>
        </Card>
      </div>
    </>
  );
};

const s = {
  heading: { margin: "0 0 20px", fontSize: 21, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  card: { padding: 28, maxWidth: 560 },
  dropzone: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10,
    border: `2px dashed ${color.primary100}`, borderRadius: radius.md, height: 140, cursor: "pointer", background: color.primary50,
  },
  dropText: { fontSize: 14.5, color: color.primary600, fontWeight: 600 },
  fileList: { margin: "16px 0 0", padding: 0, listStyle: "none" },
  fileItem: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${color.gray100}`, fontSize: 13, color: color.gray700 },
  size: { color: color.textMuted },
  uploadNote: { margin: "12px 0 0", fontSize: 12, lineHeight: 1.5, color: color.textMuted },
};

export default UploadMedia;
